import type {
  ResourceBlockReason,
  ResourceOccupancyEntry,
  ResourceOccupancyRow,
} from '@/shared/api';
import { SLOT_MINUTES } from '../resourceModel';

/** Die Belegungsabfrage deckt höchstens 31 Tage; der sichtbare Kalender kann länger sein. */
export const MAX_OCCUPANCY_DAYS = 31;

export type EntryState = 'reserved' | 'held' | 'blocked' | 'clarification' | 'cancelled';
export type CellState = 'free' | Exclude<EntryState, 'cancelled'>;

export const BLOCK_REASON_LABEL: Record<ResourceBlockReason, string> = {
  Cleaning: 'Reinigung',
  OwnerUse: 'Eigenbedarf',
  Repair: 'Reparatur',
};

export const ENTRY_STATE_LABEL: Record<EntryState, string> = {
  reserved: 'Reserviert',
  held: 'Gehalten',
  blocked: 'Gesperrt',
  clarification: 'Klärungsfall',
  cancelled: 'Aufgehoben',
};

export function entryState(entry: ResourceOccupancyEntry): EntryState {
  if (entry.status === 'Cancelled') return 'cancelled';
  if (entry.kind === 'Block') return 'blocked';
  if (entry.status === 'Held') return 'held';
  return entry.clarificationReason ? 'clarification' : 'reserved';
}

const CLARIFICATION: Record<string, string> = {
  resource_booking_not_eligible: 'Die Buchung ist storniert oder nicht mehr bestätigt.',
  resource_outside_stay: 'Der Tag liegt nicht mehr im Aufenthalt – die Abreise hat sich geändert.',
  resource_booking_missing: 'Die Buchung wurde gelöscht.',
  guest_booking_not_found: 'Die Buchung gehört inzwischen zu einem anderen Objekt.',
};

/** Warum eine bestätigte Reservierung geklärt werden muss; ein neuer Code bleibt sichtbar. */
export function clarificationText(code: string): string {
  return CLARIFICATION[code] ?? `Klärung nötig (${code})`;
}

export interface StripCell {
  startMinute: number;
  state: CellState;
}

/** Die 30-Minuten-Zellen eines Tages im Buchungsfenster der Ressource. */
export function stripCells(row: ResourceOccupancyRow, date: string): StripCell[] {
  // Ein Halt belegt wie eine Bestätigung: dieselbe Grenze wie die Exclusion im Server.
  const live = row.entries.filter((entry) => entry.date === date && entry.status !== 'Cancelled');
  const cells: StripCell[] = [];
  for (let start = row.opensAtMinute; start < row.closesAtMinute; start += SLOT_MINUTES) {
    const hit = live.find((entry) => entry.startMinute <= start && start < entry.endMinute);
    const state = hit ? entryState(hit) : 'free';
    cells.push({ startMinute: start, state: state === 'cancelled' ? 'free' : state });
  }
  return cells;
}

/** Der Tag, den der Kalender zuerst zeigt: heute, wenn er im Zeitraum liegt, sonst der erste. */
export function initialDay(dates: string[], today: string): string {
  return dates.includes(today) ? today : (dates[0] ?? today);
}
